import { Alert } from 'antd'
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { styled } from 'styled-components'
import { NOTIFICATION_DEFAULT_DURATION } from '../../constants/constants'

type NotificationType = 'success' | 'error' | 'warning' | 'info'

interface Notification {
  id: string
  type: NotificationType
  content: ReactNode
  duration?: number
  onClose?: () => void
  closable?: boolean
}

const AbsoluteNotificationContainer = styled.div`
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: max-content;
`

const StyledAlert = styled(Alert)`
  max-width: max-content;
`

export interface NotificationHandler {
  addNotification: (notification: Omit<Notification, 'id'>) => void
}

interface NotificationProviderProps {
  children: ReactNode
}

const NotificationProvider = forwardRef<
  NotificationHandler,
  NotificationProviderProps
>(({ children }, ref) => {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const addNotification = useCallback(
    (notification: Omit<Notification, 'id'>) => {
      // generate unique id for notification and display max 3 notifications at a time
      const id = Math.random().toString(36).substring(2, 15)
      setNotifications((prev) => {
        const newNotifications = [...prev, { ...notification, id }]
        return newNotifications.slice(-3)
      })
    },
    [setNotifications]
  )

  const removeNotification = useCallback(
    (id: string) => {
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== id)
      )
    },
    [setNotifications]
  )

  useEffect(() => {
    const timeouts = new Map<string, NodeJS.Timeout>()
    notifications.forEach((notification) => {
      if (notification.duration === 0) return

      const existingTimeout = timeouts.get(notification.id)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      const timeoutId = setTimeout(() => {
        removeNotification(notification.id)
        if (notification.onClose) {
          notification.onClose()
        }
      }, notification.duration || NOTIFICATION_DEFAULT_DURATION)

      timeouts.set(notification.id, timeoutId)
    })

    return () => {
      timeouts.forEach(clearTimeout)
    }
  }, [notifications, removeNotification])

  // so index.tsx can have this ref and call addNotification on it via notificationService
  useImperativeHandle(ref, () => ({
    addNotification,
  }))

  // reverse notifications to display them in the correct order (newest on top)
  const reversedNotifications = [...notifications].reverse()

  return (
    <>
      <AbsoluteNotificationContainer>
        {reversedNotifications.map((notification) => (
          <StyledAlert
            key={notification.id}
            message={notification.content}
            type={notification.type}
            showIcon
            closable={notification.closable || false}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </AbsoluteNotificationContainer>
      {children}
    </>
  )
})

export default NotificationProvider
